import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Monitor, Users, Key, Wrench, Ticket,
  AlertTriangle, Printer, Package, Gift, Shield, UserMinus,
  ClipboardList, BarChart3, Settings, LogOut, Menu, X, Activity,
  KeyRound, Inbox, ChevronDown, ChevronRight, Star, Database, Lock,
  Wifi, Phone, BarChart2, ShoppingBag, Sparkles, PiggyBank, RefreshCw, ShieldCheck
} from 'lucide-react';
import synLogo from '../assets/synergynode-logo.png';
import clsLogo from '../assets/logo.png';
import api from '../api/client';

const VERSION = 'v1.3.0';

const navGroups = [
  { label: 'Overview', icon: LayoutDashboard, items: [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/analysis', icon: BarChart2, label: 'Analysis' },
    { to: '/reports', icon: BarChart3, label: 'Reports' },
    { to: '/kpis', icon: Star, label: 'IT KPIs' },
    { to: '/ai-insights', icon: Sparkles, label: 'AI Insights' },
  ]},
  { label: 'Assets', icon: Monitor, items: [
    { to: '/assets', icon: Monitor, label: 'All Assets' },
    { to: '/in-hand', icon: Inbox, label: 'In-Hand Assets' },
    { to: '/licenses', icon: Key, label: 'Licenses' },
    { to: '/accessories', icon: Package, label: 'Accessories' },
    { to: '/toner', icon: Printer, label: 'Toner' },
    { to: '/insurance', icon: Shield, label: 'Insurance' },
  ]},
  { label: 'People', icon: Users, items: [
    { to: '/employees', icon: Users, label: 'Employees' },
    { to: '/access', icon: KeyRound, label: 'Access Management' },
    { to: '/offboarding', icon: UserMinus, label: 'Offboarding' },
  ]},
  { label: 'Operations', icon: Ticket, items: [
    { to: '/tickets', icon: Ticket, label: 'IT Tickets' },
    { to: '/accessory-requests', icon: ShoppingBag, label: 'Accessory Requests' },
    { to: '/repairs', icon: Wrench, label: 'Repairs' },
    { to: '/damages', icon: AlertTriangle, label: 'Damages' },
    { to: '/donations', icon: Gift, label: 'Donations' },
    { to: '/dongles', icon: Wifi, label: 'Dongles' },
    { to: '/voice', icon: Phone, label: 'Voice Numbers' },
  ]},
  { label: 'IT Governance', icon: ShieldCheck, items: [
    { to: '/budget', icon: PiggyBank, label: 'IT Budget' },
    { to: '/renewals', icon: RefreshCw, label: 'IT Renewals' },
    { to: '/erp-access', icon: ShieldCheck, label: 'ERP Access' },
  ]},
  { label: 'Admin', icon: Settings, items: [
    { to: '/master-files', icon: Database, label: 'Master Files' },
    { to: '/policies', icon: ClipboardList, label: 'IT Policies' },
    { to: '/audit', icon: ClipboardList, label: 'Audit Trail' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ]},
];

// General user sees only their portal
const generalUserNav = [
  { label: 'My Portal', icon: LayoutDashboard, items: [
    { to: '/', icon: LayoutDashboard, label: 'My Profile' },
    { to: '/my-tickets', icon: Ticket, label: 'My Tickets' },
    { to: '/my-accessory-requests', icon: ShoppingBag, label: 'My IT Requests' },
    { to: '/my-policies', icon: Shield, label: 'IT Policies' },
  ]}
];

// Shared My Portal section — shown to director, finance_staff, junior_it, general_user
const myPortalGroup = { label: 'My Portal', icon: LayoutDashboard, items: [
  { to: '/my-portal', icon: LayoutDashboard, label: 'My Profile' },
  { to: '/my-tickets', icon: Ticket, label: 'My Tickets' },
  { to: '/my-accessory-requests', icon: ShoppingBag, label: 'My IT Requests' },
  { to: '/my-policies', icon: Shield, label: 'IT Policies' },
]};

// Director — view only + personal portal
const directorNav = [
  myPortalGroup,
  { label: 'Overview', icon: LayoutDashboard, items: [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/analysis', icon: BarChart2, label: 'Analysis' },
    { to: '/reports', icon: BarChart3, label: 'Reports' },
    { to: '/ai-insights', icon: Sparkles, label: 'AI Insights' },
  ]},
  // IT Governance for a director is ERP Access only — IT Budget and
  // IT Renewals stay admin-only (enforced in App.js and on the API too).
  { label: 'IT Governance', icon: ShieldCheck, items: [
    { to: '/erp-access', icon: ShieldCheck, label: 'ERP Access' },
  ]},
  { label: 'Assets', icon: Monitor, items: [
    { to: '/assets', icon: Monitor, label: 'All Assets' },
    { to: '/accessories', icon: Package, label: 'Accessories' },
    { to: '/dongles', icon: Wifi, label: 'Dongles' },
    { to: '/voice', icon: Phone, label: 'Voice Numbers' },
  ]},
  { label: 'People', icon: Users, items: [
    { to: '/employees', icon: Users, label: 'Employees' },
  ]},
];

// Finance Staff — view only: All Assets, Accessories, Dongles, Voice + Reports
const financeNav = [
  myPortalGroup,
  { label: 'Assets', icon: Monitor, items: [
    { to: '/assets', icon: Monitor, label: 'All Assets' },
    { to: '/accessories', icon: Package, label: 'Accessories' },
    { to: '/dongles', icon: Wifi, label: 'Dongles' },
    { to: '/voice', icon: Phone, label: 'Voice Numbers' },
  ]},
  { label: 'Reports', icon: BarChart3, items: [
    { to: '/reports', icon: BarChart3, label: 'Reports' },
  ]},
];

// IT Staff — same as admin nav but without Analysis (/analysis), Access Management (/access),
// and the admin-only IT Governance group (IT Budget / IT Renewals / ERP Access)
const itStaffNav = navGroups
  .map(g => ({ ...g, items: g.items.filter(item => item.to !== '/analysis' && item.to !== '/access') }))
  .filter(g => g.items.length > 0 && g.label !== 'Admin' && g.label !== 'IT Governance');

// Junior IT — personal portal + IT Tickets module only
const juniorItNav = [
  myPortalGroup,
  { label: 'Operations', icon: Ticket, items: [
    { to: '/tickets', icon: Ticket, label: 'IT Tickets' },
    { to: '/accessory-requests', icon: ShoppingBag, label: 'Accessory Requests' },
  ]},
];

function getGreeting(name) {
  const h = new Date().getHours();
  const time = h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
  return `${time}, ${name?.split(' ')[0] || 'there'} 👋`;
}

const GROUP_COLORS = {
  'Overview':  '#3b82f6',
  'Assets':    '#10b981',
  'People':    '#8b5cf6',
  'Operations':'#f59e0b',
  'IT Governance': '#34d399',
  'Admin':     '#64748b',
  'My Portal': '#60a5fa',
  'Reports':   '#a78bfa',
};

function NavGroup({ group, defaultOpen = true, locked = false }) {
  const location = useLocation();
  const isGroupActive = group.items.some(item =>
    item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)
  );
  // Active group is always forced open — can't accidentally close the section you're in
  const [open, setOpen] = useState(defaultOpen || locked || isGroupActive);
  const color = GROUP_COLORS[group.label] || '#475569';
  const canToggle = !locked && !isGroupActive;

  return (
    <div style={{ marginBottom: 1 }}>
      {/* Section header */}
      <button
        onClick={() => canToggle && setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 7,
          padding: '6px 8px', borderRadius: 8, background: 'none', border: 'none',
          cursor: canToggle ? 'pointer' : 'default',
          marginBottom: 2,
        }}
        onMouseEnter={e => { if (canToggle) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
      >
        {/* Colored accent bar */}
        <div style={{
          width: 3, height: 14, borderRadius: 2, flexShrink: 0,
          background: color,
          opacity: isGroupActive ? 1 : 0.35,
          transition: 'opacity 0.2s',
        }}/>
        <span style={{
          flex: 1, textAlign: 'left', fontSize: 10, fontWeight: 700,
          letterSpacing: '0.09em', textTransform: 'uppercase',
          color: isGroupActive ? '#94a3b8' : '#334155',
          transition: 'color 0.2s',
        }}>
          {group.label}
        </span>
        {canToggle && (
          <ChevronDown size={11} style={{
            color: '#334155', flexShrink: 0,
            transition: 'transform 0.22s ease',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}/>
        )}
      </button>

      {/* Items — animated height */}
      <div style={{
        overflow: 'hidden',
        maxHeight: (open || locked || isGroupActive) ? '800px' : '0px',
        transition: 'max-height 0.28s ease',
        paddingLeft: 4,
        marginBottom: (open || locked || isGroupActive) ? 6 : 0,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {group.items.map(({ to, icon: Icon, label }) => (
            <NavLink key={to + label} to={to} end={to === '/'}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Icon size={13} style={{ flexShrink: 0 }}/><span>{label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [time, setTime] = useState(new Date());
  const [logoFlipped, setLogoFlipped] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', newPass: '', confirm: '' });
  const [pwdError, setPwdError] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdSuccess, setPwdSuccess] = useState(false);
  const [showInactiveWarning, setShowInactiveWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(120);
  const inactiveTimer = useRef(null);
  const warningTimer = useRef(null);
  const countdownInterval = useRef(null);

  const INACTIVE_MS = 5 * 60 * 60 * 1000;   // 5 hours
  const WARNING_BEFORE_MS = 2 * 60 * 1000;   // warn 2 min before logout

  const doLogout = useCallback(async () => {
    setShowInactiveWarning(false);
    await logout();
    navigate('/login');
  }, [logout, navigate]);

  const resetInactivityTimer = useCallback(() => {
    if (showInactiveWarning) return; // don't reset once warning is visible
    clearTimeout(inactiveTimer.current);
    clearTimeout(warningTimer.current);
    inactiveTimer.current = setTimeout(() => {
      // Show warning 2 min before auto-logout
      setWarningCountdown(120);
      setShowInactiveWarning(true);
      countdownInterval.current = setInterval(() => {
        setWarningCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current);
            doLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVE_MS - WARNING_BEFORE_MS);
  }, [showInactiveWarning, doLogout, INACTIVE_MS, WARNING_BEFORE_MS]);

  const stayLoggedIn = () => {
    clearInterval(countdownInterval.current);
    setShowInactiveWarning(false);
    resetInactivityTimer();
  };

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer(); // start on mount
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimeout(inactiveTimer.current);
      clearTimeout(warningTimer.current);
      clearInterval(countdownInterval.current);
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    const clock = setInterval(() => setTime(new Date()), 60000);
    const flip = setInterval(() => {
      setLogoFlipped(true);
      // Remove class after animation completes so it can be re-triggered
      setTimeout(() => setLogoFlipped(false), 750);
    }, 8000);
    return () => { clearInterval(clock); clearInterval(flip); };
  }, []);

  // Load user's accessible companies for header display
  useEffect(() => {
    if (user && !['admin','it_staff'].includes(user.role)) {
      api.get(`/master-files/user-access/${user.id}`)
        .then(r => setCompanies(r.data || []))
        .catch(() => {});
    }
  }, [user]);

  const handleChangePassword = async e => {
    e.preventDefault();
    if (pwdForm.newPass !== pwdForm.confirm) return setPwdError('Passwords do not match');
    if (pwdForm.newPass.length < 8) return setPwdError('Minimum 8 characters');
    setPwdSaving(true); setPwdError('');
    try {
      await api.post('/auth/change-password', { currentPassword: pwdForm.current, newPassword: pwdForm.newPass });
      setPwdSuccess(true); setPwdForm({ current: '', newPass: '', confirm: '' });
      setTimeout(() => { setShowPwdModal(false); setPwdSuccess(false); }, 2000);
    } catch (err) { setPwdError(err.response?.data?.error || 'Failed'); }
    finally { setPwdSaving(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const groups =
    user?.role === 'general_user'  ? generalUserNav :
    user?.role === 'director'      ? directorNav :
    user?.role === 'finance_staff' ? financeNav :
    user?.role === 'junior_it'     ? juniorItNav :
    user?.role === 'it_staff'      ? itStaffNav :
    navGroups.filter(g => g.label !== 'Admin' || isAdmin);

  const timeStr = time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  const dateStr = time.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="flex h-screen overflow-hidden bg-mesh">
      {/* Sidebar */}
      <aside className="flex flex-col flex-shrink-0 transition-all duration-300 overflow-hidden"
        style={{ width: sidebarOpen ? 224 : 0, background: 'linear-gradient(180deg,#080f1e 0%,#060c18 100%)', borderRight: '1px solid rgba(255,255,255,0.05)' }}>

        {/* Logo — with flip animation */}
        <div className="px-3 pt-4 pb-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
          <div className="flex flex-col items-center gap-2">
            <div className="relative w-full" style={{ perspective: '600px' }}>
              {/* Subtle glow behind logo */}
              <div className="absolute inset-0 pointer-events-none rounded-xl" style={{ background: 'rgba(59,130,246,0.12)', filter: 'blur(14px)', transform: 'scale(1.1)' }}/>
              <img
                src={synLogo}
                alt="SynergyNode"
                className={logoFlipped ? 'logo-flip' : ''}
                style={{
                  width: '100%', height: 'auto', objectFit: 'contain', borderRadius: 10,
                  background: 'rgba(255,255,255,0.94)', padding: '7px 10px',
                  display: 'block', position: 'relative',
                  filter: 'drop-shadow(0 0 8px rgba(59,130,246,0.45))',
                  border: '1px solid rgba(59,130,246,0.2)',
                  boxShadow: '0 0 20px rgba(59,130,246,0.15)',
                }}
              />
            </div>
            <div className="text-xs flex items-center justify-center gap-1.5" style={{ color: '#1e3a5f' }}>
              <Activity size={9} style={{ color: '#10b981' }} />
              <span style={{ color: '#10b981' }}>Online</span>
              <span>·</span>
              <span>{VERSION}</span>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}>
          <div>
            {groups.map(group => {
              const isViewOnlyRole = ['director','finance_staff'].includes(user?.role);
              const isJuniorIT = user?.role === 'junior_it';
              const isFullAccess = ['admin','it_staff'].includes(user?.role);
              // My Portal and People locked for Director/Finance; My Portal and Operations locked for Junior IT
              const locked = (isViewOnlyRole && ['My Portal','People'].includes(group.label))
                || (isJuniorIT && ['My Portal','Operations'].includes(group.label));
              // Admin and IT staff — all groups start open; others keep existing rules
              const defaultOpen = isFullAccess
                || user?.role === 'general_user'
                || isJuniorIT
                || locked;
              return (
                <NavGroup key={group.label} group={group}
                  defaultOpen={defaultOpen}
                  locked={locked}/>
              );
            })}
          </div>
        </nav>

        {/* CLS Group badge */}
        <div className="px-3 py-2 flex items-center gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <img src={clsLogo} alt="CL Synergy Group" style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 5, background: 'rgba(255,255,255,0.85)', padding: 2, flexShrink: 0 }}/>
          <span className="text-xs" style={{ color: '#1e3a5f', lineHeight: 1.3 }}>A CL Synergy Group IT Product</span>
        </div>

        {/* User footer */}
        <div className="p-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-2.5 px-2 mb-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 relative"
              style={{ background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)' }}>
              {user?.full_name?.charAt(0) || 'U'}
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2" style={{ borderColor: '#060c18' }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate" style={{ color: '#e2e8f0' }}>{user?.full_name}</div>
              <div className="text-xs capitalize" style={{ color: '#1e3a5f' }}>{user?.role?.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <button onClick={() => { setPwdForm({current:'',newPass:'',confirm:''}); setPwdError(''); setPwdSuccess(false); setShowPwdModal(true); }} className="sidebar-link w-full text-xs"><Lock size={12}/>Change Password</button>
          <button onClick={handleLogout} className="sidebar-link w-full text-xs"><LogOut size={12}/>Logout</button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="flex items-center gap-4 px-5 py-3 flex-shrink-0"
          style={{ background: 'rgba(6,11,24,0.85)', borderBottom: '1px solid rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)' }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="btn-ghost p-2">
            {sidebarOpen ? <X size={15}/> : <Menu size={15}/>}
          </button>

          <div className="flex-1">
            <div className="text-xl font-bold gradient-text">{getGreeting(user?.full_name)}</div>
            {['admin','it_staff'].includes(user?.role) ? (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs px-2.5 py-0.5 rounded-full font-semibold"
                  style={{background:'rgba(16,185,129,0.12)',color:'#34d399',border:'1px solid rgba(16,185,129,0.2)'}}>
                  All Companies
                </span>
                <span className="text-xs" style={{color:'#1e3a5f'}}>· Full data access</span>
              </div>
            ) : companies.length > 0 ? (
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs" style={{color:'#334155'}}>Access:</span>
                {companies.map(c => (
                  <span key={c.company_id} className="text-xs px-2.5 py-0.5 rounded-full font-semibold"
                    style={{background:'rgba(59,130,246,0.15)',color:'#60a5fa',border:'1px solid rgba(59,130,246,0.25)'}}>
                    {c.name}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-xs mt-0.5" style={{color:'#1e3a5f'}}>CL Synergy Group · IT Asset Management</div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-semibold tabular-nums" style={{ color: '#60a5fa' }}>{timeStr}</div>
              <div className="text-xs" style={{ color: '#1e3a5f' }}>{dateStr}</div>
            </div>
            <div className="w-px h-8" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1.5"
              style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              {VERSION}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 page-enter">
          {/* Point 4 & 5: No company or no role configured — show blank/pending screen */}
          {user?.role === 'general_user' &&
           !user?.company_id &&
           !user?.employee_id &&
           companies.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-6 text-center">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.2)'}}>
                <Lock size={32} style={{color:'#f59e0b'}}/>
              </div>
              <div>
                <div className="text-xl font-bold mb-2" style={{color:'#f59e0b'}}>Account Setup Pending</div>
                <div className="text-sm max-w-sm" style={{color:'#475569'}}>
                  Your account is active but no company or data access has been assigned yet.
                  Please contact your IT Administrator to complete your account setup.
                </div>
              </div>
              <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.15)',color:'#92400e'}}>
                Admin: Go to <strong>Settings → Users</strong> → select this user → assign Company Access and Role
              </div>
            </div>
          ) : children}
        </main>
      </div>

      {/* Inactivity Warning Modal */}
      {showInactiveWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{background:'rgba(4,8,16,0.9)',backdropFilter:'blur(8px)'}}>
          <div className="w-full max-w-sm rounded-2xl overflow-hidden text-center" style={{background:'linear-gradient(135deg,#0d1528,#0a1020)',border:'1px solid rgba(245,158,11,0.3)',boxShadow:'0 24px 80px rgba(0,0,0,0.9)'}}>
            <div className="px-6 py-5" style={{borderBottom:'1px solid rgba(255,255,255,0.07)',background:'rgba(245,158,11,0.08)'}}>
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{background:'#f59e0b'}}/>
                <span className="text-sm font-bold" style={{color:'#fbbf24'}}>Session Inactivity Warning</span>
              </div>
              <p className="text-xs" style={{color:'#94a3b8'}}>You will be signed out due to inactivity</p>
            </div>
            <div className="px-6 py-6">
              <div className="text-5xl font-mono font-bold mb-1" style={{color: warningCountdown <= 30 ? '#ef4444' : '#f59e0b'}}>
                {Math.floor(warningCountdown / 60)}:{String(warningCountdown % 60).padStart(2, '0')}
              </div>
              <p className="text-xs mb-6" style={{color:'#64748b'}}>minutes remaining before automatic sign-out</p>
              <button
                onClick={stayLoggedIn}
                className="w-full py-2.5 rounded-xl text-sm font-semibold mb-2"
                style={{background:'linear-gradient(135deg,#1e3a8a,#1d4ed8)',color:'#fff',border:'none',cursor:'pointer'}}>
                Stay Signed In
              </button>
              <button
                onClick={doLogout}
                className="w-full py-2 rounded-xl text-xs"
                style={{background:'transparent',color:'#64748b',border:'1px solid rgba(255,255,255,0.07)',cursor:'pointer'}}>
                Sign Out Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPwdModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(4,8,16,0.85)',backdropFilter:'blur(8px)'}}>
          <div className="w-full max-w-sm rounded-2xl overflow-hidden" style={{background:'linear-gradient(135deg,#0d1528,#0a1020)',border:'1px solid rgba(255,255,255,0.1)',boxShadow:'0 24px 80px rgba(0,0,0,0.8)',animation:'slideUp 0.3s ease-out'}}>
            <div className="flex items-center justify-between px-6 py-4" style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
              <div className="flex items-center gap-2">
                <Lock size={15} style={{color:'#60a5fa'}}/>
                <span className="font-semibold text-sm" style={{color:'#e2e8f0'}}>Change Password</span>
              </div>
              <button onClick={() => { setShowPwdModal(false); setPwdError(''); setPwdForm({current:'',newPass:'',confirm:''}); setPwdSuccess(false); }} className="btn-ghost p-1.5"><X size={15}/></button>
            </div>
            <div className="px-6 py-5">
              {pwdSuccess ? (
                <div className="text-center py-4">
                  <div className="text-3xl mb-3">✅</div>
                  <div className="font-semibold" style={{color:'#34d399'}}>Password changed successfully</div>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  {pwdError && <div className="px-3 py-2 rounded-xl text-sm" style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',color:'#f87171'}}>{pwdError}</div>}
                  {[['Current Password','current'],['New Password','newPass'],['Confirm New Password','confirm']].map(([label,key]) => (
                    <div key={key}>
                      <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>{label}</label>
                      <input type="password" required value={pwdForm[key]}
                        onChange={e => setPwdForm(prev => ({...prev,[key]:e.target.value}))}
                        className="input-dark"/>
                    </div>
                  ))}
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={() => setShowPwdModal(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
                    <button type="submit" disabled={pwdSaving} className="btn-primary flex-1 justify-center">{pwdSaving?'Updating...':'Update Password'}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
